<template>
  <AppShell>
    <AppPage
      :crumbs="crumbs"
      :title="mainGroup?.proj || 'Finanz-Dashboard'"
      icon="fa-chart-column"
      icon-color="#23799D"
      :period="periodLabel"
      :tabs="TABS"
      :view="selectedTab"
      :loading="loading"
      :error="error"
      :retryable="false"
      loading-text="Lade Dashboard-Daten…"
      @update:view="setTab"
    >
      <!-- Summierte Projekte (bisher "+ Marke › Projekt" in der Breadcrumb) -->
      <template #lozenges>
        <StatusLozenge v-for="group in compareGroups" :key="`${group.marke}-${group.proj}`" tone="discovery" :label="`+ ${group.proj}`" />
      </template>

      <template #toolbar>
        <FilterBar
          :filters="vm.filters"
          :selected-bereich="selectedBereich"
          :selected-marke="selectedMarke"
          :selected-projekt="selectedProjekt"
          :selected-year="selectedYear"
          :month-label="vm.metadata.monthLabel"
          @update="updateSelection"
        />
        <ComparePanel
          v-if="selectedTab === 'dashboard'"
          :grouped-brand-projects="vm.groupedBrandProjects"
          :selected-compare-ids="selectedCompareIds"
          @toggle="toggleCompare"
          @set-brand="setCompareByBrand"
          @clear="clearCompare"
        />
      </template>

      <p v-if="!hasData" class="jr-empty">Keine Dashboard-Daten verfügbar.</p>

      <!-- Dashboard: Kennzahl-Karten · Ergebnistabelle · Effizienz -->
      <template v-else-if="selectedTab === 'dashboard'">
        <div class="jr-stats jr-stats--cols-4">
          <StatCard
            v-for="(card, index) in vm.summaryCards"
            :key="card.key"
            :label="card.label"
            :value="card.value"
            :unit="card.unit || ''"
            :meta="card.meta"
            :icon="SUMMARY_ICONS[index % SUMMARY_ICONS.length].icon"
            :tone="SUMMARY_ICONS[index % SUMMARY_ICONS.length].tone"
          />
        </div>

        <section class="jr-card jr-card--flush">
          <MetricsTable :rows="vm.metricsRows" />
        </section>

        <div class="jr-stats jr-stats--cols-2">
          <StatCard
            v-for="card in vm.kpiCards"
            :key="card.key"
            :label="card.label"
            :value="card.value"
            :unit="card.unit || ''"
            :meta="card.meta"
            icon="fa-stopwatch"
            tone="blue"
          />
        </div>
      </template>

      <!-- Verlauf: Jahresansicht mit Diagramm und Monatstabelle -->
      <section v-else class="jr-card">
        <TimeseriesView :projekt-nummer="selectedProjekt" :year="selectedYear" />
      </section>
    </AppPage>
  </AppShell>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import AppShell from '@/components/shell/AppShell.vue';
import AppPage from '@/components/ui/AppPage.vue';
import StatCard from '@/components/ui/StatCard.vue';
import StatusLozenge from '@/components/ui/StatusLozenge.vue';
import FilterBar from '../components/FilterBar.vue';
import ComparePanel from '../components/ComparePanel.vue';
import MetricsTable from '../components/MetricsTable.vue';
import TimeseriesView from '../components/TimeseriesView.vue';
import { useDashboardState } from '../composables/useDashboardState';

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: 'fa-table-cells-large' },
  { key: 'verlauf', label: 'Verlauf', icon: 'fa-chart-line' }
];

// Icons der vier Kennzahl-Karten: Sales Erlöse · Umsatz · Cockpit Ergebnis · Marge
const SUMMARY_ICONS = [
  { icon: 'fa-cart-shopping', tone: 'blue' },
  { icon: 'fa-euro-sign', tone: 'teal' },
  { icon: 'fa-scale-balanced', tone: 'purple' },
  { icon: 'fa-percent', tone: 'green' }
];

const {
  loading,
  error,
  selectedBereich,
  selectedMarke,
  selectedProjekt,
  selectedCompareIds,
  selectedYear,
  selectedTab,
  hasData,
  vm,
  initialize,
  updateSelection,
  setTab,
  toggleCompare,
  clearCompare,
  setCompareByBrand
} = useDashboardState();

const mainGroup = computed(() => vm.value.breadcrumbGroups[0] || null);
const compareGroups = computed(() => vm.value.breadcrumbGroups.slice(1));

const crumbs = computed(() => [
  { label: 'Finanzen', to: { name: 'dashboard' } },
  { label: vm.value.bereichLabel },
  ...(mainGroup.value ? [{ label: mainGroup.value.marke }] : [])
]);

// Berichtszeitraum im Dashboard, Jahresansicht im Verlauf.
const periodLabel = computed(() => (selectedTab.value === 'verlauf'
  ? `Jahresansicht ${selectedYear.value}`
  : vm.value.metadata.reportingPeriodLabel));

onMounted(initialize);
</script>
